import { useCallback, useMemo } from 'react';
import { FlightRoomUserStatus, JobTitle, MessageMention, MessageMentionType } from '@connect-x-app/shared-domain-models';
import { getCustomCrewTitle, getMentionsRegex, UserUtil } from '@connect-x-app/shared-utils';

import { useExternalMentionsStore } from '../stores';
import { useChannels, useChatMessage, useUsers } from './feature';
import { useAuthentication } from './useAuthentication';

export const useMentions = (mentionInProgress = '') => {
  const { selectedChannel } = useChannels();
  const { users } = useUsers();
  const { selectedMentions } = useChatMessage();
  const { externalMentions } = useExternalMentionsStore();
  const { canMentionExternally } = useAuthentication();

  const groupUsersByRole = (
    roleMap: Map<string, MessageMention>,
    role: string,
    userExternalId: string,
    type: MessageMentionType,
  ) => {
    if (!roleMap.has(role)) {
      roleMap.set(role, {
        label: role,
        type,
        users: [],
      });
    }
    roleMap.get(role)!.users?.push(userExternalId);
  };

  const crewRoleMentionOptions = useMemo(() => {
    if (!selectedChannel?.users) {
      return [];
    }

    const roleMap = new Map<string, MessageMention>();

    const handleScheduledUser = (role: string, externalId: string) => {
      groupUsersByRole(roleMap, role, externalId, MessageMentionType.CREW_ROLE);
    };

    const handleUnscheduledUser = (externalId: string, channelUserJobTitle: JobTitle) => {
      const user = users.find(u => u.externalId === externalId);
      const role = UserUtil.getDivision(channelUserJobTitle ?? user?.jobTitle).toUpperCase();
      groupUsersByRole(roleMap, role, externalId, MessageMentionType.CREW_ROLE);
    };

    selectedChannel.users.forEach(({ externalId, jobTitle, jobTitleCustom, status }) => {
      if (status === FlightRoomUserStatus.PENDING) {
        return;
      }

      const customTitle = getCustomCrewTitle(jobTitle, jobTitleCustom);

      if (customTitle) {
        handleScheduledUser(customTitle, externalId);
      } else if (status === FlightRoomUserStatus.SCHEDULED) {
        handleScheduledUser(UserUtil.getDivision(jobTitle), externalId);
      } else {
        handleUnscheduledUser(externalId, jobTitle);
      }
    });

    return Array.from(roleMap.values());
  }, [selectedChannel?.users, users]);

  const crewNameMentionOptions = useMemo(() => {
    if (!selectedChannel?.users) {
      return [];
    }

    return selectedChannel.users.reduce<MessageMention[]>((acc, flightRoomUser) => {
      if (flightRoomUser.status === FlightRoomUserStatus.PENDING) {
        return acc;
      }
      const user = users.find(({ externalId }) => externalId === flightRoomUser.externalId);
      if (user?.firstName && user.lastName) {
        acc.push({
          label: `${user.firstName} ${user.lastName}`,
          type: MessageMentionType.USER_NAME,
          users: [user.externalId],
        });
      }
      return acc;
    }, []);
  }, [selectedChannel, users]);

  const crewDisplayNameMentionOptions: MessageMention[] = useMemo(() => {
    if (!selectedChannel?.users) {
      return [];
    }

    const roleMap = new Map<string, MessageMention>();

    selectedChannel.users.forEach(flightRoomUser => {
      if (flightRoomUser.status === FlightRoomUserStatus.PENDING) {
        return;
      }
      const user = users.find(({ externalId }) => externalId === flightRoomUser.externalId);
      user?.roles?.forEach(role => {
        if (role.canBeMentioned) {
          groupUsersByRole(roleMap, role.displayName, flightRoomUser.externalId, MessageMentionType.DISPLAY_NAME);
        }
      });
    });

    return Array.from(roleMap.values());
  }, [selectedChannel?.users, users]);

  const mentionOptions: MessageMention[] = useMemo(
    () =>
      [...crewRoleMentionOptions, ...crewNameMentionOptions, ...crewDisplayNameMentionOptions].sort((a, b) =>
        a.label.localeCompare(b.label),
      ), // Sort alphabetically by label
    [crewRoleMentionOptions, crewNameMentionOptions, crewDisplayNameMentionOptions],
  );

  const filteredMentionOptions: MessageMention[] = useMemo(() => {
    return mentionOptions.filter(mention =>
      mention.label.toUpperCase().includes(mentionInProgress.toUpperCase().split('@')[1] || ''),
    );
  }, [mentionOptions, mentionInProgress]);

  const mentionsRegex = useMemo(() => {
    return getMentionsRegex(selectedMentions ?? []);
  }, [selectedMentions]);

  const getUsedMentions = useCallback(
    (message = ''): MessageMention[] => {
      const foundMentions: string[] = [];
      let match;
      const regex = new RegExp(mentionsRegex, 'gi');

      while ((match = regex.exec(message)) !== null) {
        foundMentions.push(match[0]);
      }

      return foundMentions
        .map(mention => selectedMentions.find(option => mention === `@${option.label}`))
        .filter((maybeMention): maybeMention is MessageMention => !!maybeMention);
    },
    [mentionsRegex, selectedMentions],
  );

  const internalMentionLabels: Set<string> = useMemo(() => {
    return new Set(mentionOptions.filter(mention => mention.type === MessageMentionType.DISPLAY_NAME).map(m => m.label));
  }, [mentionOptions]);

  const filteredExternalMentions: MessageMention[] = useMemo(() => {
    if (!canMentionExternally) {
      return [];
    }
    return externalMentions.filter(mention => !internalMentionLabels.has(mention.label));
  }, [canMentionExternally, externalMentions, internalMentionLabels]);

  const filteredExternalLabels: Set<string> = useMemo(
    () => new Set(filteredExternalMentions.map(m => m.label)),
    [filteredExternalMentions],
  );

  const shouldShowExternalMentionModal = useCallback(
    (usedMentions: MessageMention[]) => usedMentions.some(m => filteredExternalLabels.has(m.label)),
    [filteredExternalLabels],
  );

  const filteredExternalMentionOptions: MessageMention[] = useMemo(() => {
    return filteredExternalMentions
      .filter(mention => {
        return mention.label.toUpperCase().includes(mentionInProgress.toUpperCase().split('@')[1] || '');
      })
      .slice(0, 20);
  }, [filteredExternalMentions, mentionInProgress]);

  return {
    filteredExternalMentionOptions,
    filteredMentionOptions,
    getUsedMentions,
    mentionOptions,
    mentionsRegex,
    shouldShowExternalMentionModal,
  };
};
